import { useState, useEffect } from 'react';
import { DeliveryModeAPI, PaymentModeAPI, CountryAPI, CityAPI, BankAPI } from '../models/listsAPI';

export const useDeliveryModes = () => {
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchModes = async () => {
      try {
        setLoading(true);
        const data = await DeliveryModeAPI.getAll();
        // Filter active ones and extract just the name for backward compatibility
        const activeModes = data.filter(m => m.active).map(m => m.name);
        setModes(activeModes);
      } catch (err) {
        setError(err);
        console.error('Error fetching delivery modes:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchModes();
  }, []);

  return { modes, loading, error };
};

// Returns full delivery mode objects including descriptions
export const useDeliveryModesWithDesc = () => {
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchModes = async () => {
      try {
        setLoading(true);
        const data = await DeliveryModeAPI.getAll();
        // Filter active ones and return full objects
        const activeModes = data.filter(m => m.active);
        setModes(activeModes);
      } catch (err) {
        setError(err);
        console.error('Error fetching delivery modes:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchModes();
  }, []);

  // Helper to get description by mode name
  const getDescByName = (modeName) => {
    if (!modeName) return null;
    const mode = modes.find(m => m.name === modeName);
    return mode?.desc || null;
  };

  return { modes, loading, error, getDescByName };
};

export const usePaymentModes = () => {
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchModes = async () => {
      try {
        setLoading(true);
        const data = await PaymentModeAPI.getAll();
        const activeModes = data.filter(m => m.active).map(m => m.name);
        setModes(activeModes);
      } catch (err) {
        setError(err);
        console.error('Error fetching payment modes:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchModes();
  }, []);

  return { modes, loading, error };
};

export const useCountries = () => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const data = await CountryAPI.getAll();
        const activeCountries = data.filter(c => c.active).map(c => c.name);
        setCountries(activeCountries);
      } catch (err) {
        setError(err);
        console.error('Error fetching countries:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCountries();
  }, []);

  return { countries, loading, error };
};

export const useCities = () => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        setLoading(true);
        const data = await CityAPI.getAll();
        const activeCities = data.filter(c => c.active).map(c => c.name);
        setCities(activeCities);
      } catch (err) {
        setError(err);
        console.error('Error fetching cities:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCities();
  }, []);

  return { cities, loading, error };
};

export const useBanks = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        setLoading(true);
        const data = await BankAPI.getAll();
        const activeBanks = data.filter(b => b.active).map(b => b.name);
        setBanks(activeBanks);
      } catch (err) {
        setError(err);
        console.error('Error fetching banks:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBanks();
  }, []);

  return { banks, loading, error };
};

